import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { X, Search, Loader2, AlertCircle, ArrowLeft, ArrowRight, Check, ShoppingBag, User, Phone, Hash } from "lucide-react";
import { useCounties, useCities } from "../hooks/useGeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPES = [
  { v: "garantie", l: "Garanție", d: "Produsul se ridică, se verifică, apoi se livrează înapoi (2 AWB-uri secvențiale)" },
  { v: "retur", l: "Retur", d: "Produsul se ridică, la sosire se returnează banii pe IBAN (1 AWB)" },
  { v: "schimb", l: "Schimb", d: "2 AWB-uri Sameday: livrare colet nou + ridicare colet vechi (urmărite separat)" },
  { v: "livrare", l: "Livrare", d: "Livrare simplă către client (1 AWB · opțional cu ramburs)" },
];

const PLATFORM_LABEL = {
  emag_ro_fbe: "EMAG-RO-FBE", emag_ro_bau: "EMAG-RO-BAU",
  emag_bg_fbe: "EMAG-BG-FBE", emag_bg_bau: "EMAG-BG-BAU",
  emag_hu_fbe: "EMAG-HU-FBE", emag_hu_bau: "EMAG-HU-BAU",
};

const EMPTY = {
  type: "garantie",
  order_id: "",
  platform: "",
  customer_name: "", customer_phone: "", customer_email: "", customer_iban: "",
  customer_address: "", customer_city: "", customer_county: "", customer_postal_code: "",
  pickup_address: "", pickup_city: "", pickup_county: "", pickup_postal_code: "",
  problem: "",
  value: "", currency: "RON",
  products: [],
};

export default function NewReturnRequestModal({ onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1=search, 2=form
  const [searchOrder, setSearchOrder] = useState("");
  const [searchName, setSearchName] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runSearch = async () => {
    if (!searchOrder && !searchName && !searchPhone) {
      setError("Introdu cel puțin un criteriu de căutare.");
      return;
    }
    setSearching(true);
    setError("");
    try {
      const r = await axios.get(`${API}/returns/search-orders`, {
        params: { order_id: searchOrder || undefined, name: searchName || undefined, phone: searchPhone || undefined },
      });
      setResults(r.data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSearching(false);
    }
  };

  const pickOrder = async (item) => {
    setError("");
    try {
      const r = await axios.get(`${API}/returns/from-order/${item.order_id}`);
      // Client probably ordered to easybox — adresa va fi cerută clientului
      // și completată manual. Restul câmpurilor le luăm din comandă.
      const { customer_address, customer_city, customer_county, customer_postal_code, ...rest } = r.data;
      setForm((f) => ({ ...f, ...rest, value: rest.value ?? "" }));
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const startManual = () => {
    setForm(EMPTY);
    setStep(2);
  };

  const submit = async () => {
    if (!form.customer_name) { setError("Numele clientului este obligatoriu."); return; }
    if (!form.type) { setError("Selectează tipul cererii."); return; }
    setCreating(true); setError("");
    try {
      const body = { ...form, status: "pending", value: form.value === "" || form.value == null ? null : Number(form.value) };
      const r = await axios.post(`${API}/returns`, body);
      onCreated?.(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="new-return-modal" className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-6">
      <div className="flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-zinc-800 px-5 py-3.5">
          <div>
            <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-zinc-500">
              <Step n={1} label="Caută comanda" active={step === 1} done={step > 1} />
              <ArrowRight className="h-3 w-3 text-zinc-700" />
              <Step n={2} label="Detalii cerere" active={step === 2} done={false} />
            </div>
            <h3 className="mt-1 text-lg font-semibold text-zinc-100">Cerere nouă</h3>
          </div>
          <button data-testid="new-return-close" onClick={onClose} className="rounded-md border border-zinc-800 bg-zinc-900 p-1.5 text-zinc-400 hover:text-zinc-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {error && (
            <div className="mb-3 flex items-start gap-2 rounded border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5" /> <span className="flex-1">{error}</span>
              <button onClick={() => setError("")}><X className="h-3.5 w-3.5" /></button>
            </div>
          )}

          {step === 1 && (
            <Step1
              {...{ searchOrder, setSearchOrder, searchName, setSearchName, searchPhone, setSearchPhone,
                searching, runSearch, results, pickOrder, startManual }}
            />
          )}

          {step === 2 && (
            <Step2 form={form} set={set} setForm={setForm} />
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-zinc-800 px-5 py-3">
          <button onClick={() => step === 2 ? setStep(1) : onClose()}
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:text-zinc-100">
            <ArrowLeft className="h-3.5 w-3.5" /> {step === 2 ? "Înapoi la căutare" : "Anulează"}
          </button>
          {step === 2 && (
            <button data-testid="new-return-create" onClick={submit} disabled={creating}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-zinc-950 hover:bg-emerald-400 disabled:opacity-50">
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Creează cererea
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ n, label, active, done }) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${active ? "text-orange-300" : done ? "text-emerald-300" : "text-zinc-500"}`}>
      <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold ${active ? "bg-orange-500/20 text-orange-200" : done ? "bg-emerald-500/20 text-emerald-200" : "bg-zinc-800 text-zinc-500"}`}>
        {done ? <Check className="h-2.5 w-2.5" /> : n}
      </span>
      {label}
    </span>
  );
}

// ---------- STEP 1: SEARCH ----------
function Step1({ searchOrder, setSearchOrder, searchName, setSearchName, searchPhone, setSearchPhone, searching, runSearch, results, pickOrder, startManual }) {
  return (
    <div>
      <p className="mb-4 text-sm text-zinc-400">Caută comanda existentă în sistem după unul dintre criterii. Dacă nu o găsești, poți adăuga manual.</p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <SearchField icon={Hash} label="Nr. comandă" testId="search-order" value={searchOrder} onChange={setSearchOrder} placeholder="ex: 48315216" onEnter={runSearch} />
        <SearchField icon={User} label="Nume client" testId="search-name" value={searchName} onChange={setSearchName} placeholder="ex: Popescu" onEnter={runSearch} />
        <SearchField icon={Phone} label="Telefon" testId="search-phone" value={searchPhone} onChange={setSearchPhone} placeholder="ex: 0744..." onEnter={runSearch} />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button data-testid="search-orders-btn" onClick={runSearch} disabled={searching}
          className="inline-flex items-center gap-1.5 rounded-md bg-orange-500 px-3 py-2 text-xs font-semibold text-zinc-950 hover:bg-orange-400 disabled:opacity-50">
          {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Caută în comenzi
        </button>
        <button data-testid="start-manual-btn" onClick={startManual}
          className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-300 hover:border-orange-500/40 hover:text-orange-300">
          Adaugă manual (clientul nu există)
        </button>
      </div>

      {results !== null && (
        <div className="mt-5">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Rezultate ({results.length})</h4>
          {results.length === 0 && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-8 text-center text-sm text-zinc-500">
              Nicio comandă găsită. Apasă „Adaugă manual” pentru a introduce datele manual.
            </div>
          )}
          <div className="space-y-2">
            {results.map((it) => (
              <button key={it.order_id} data-testid={`search-result-${it.order_id}`} onClick={() => pickOrder(it)}
                className="group flex w-full items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-left transition hover:border-orange-500/40 hover:bg-zinc-900">
                <ShoppingBag className="h-4 w-4 shrink-0 text-zinc-500 group-hover:text-orange-300" />
                <div className="min-w-0 flex-1 grid grid-cols-4 gap-2 text-xs">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-600">Comandă</p>
                    <p className="font-mono text-zinc-100">#{it.order_id}</p>
                    {it.platform && <p className="font-mono text-[9px] text-orange-300/80">{PLATFORM_LABEL[it.platform] || it.platform}</p>}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-600">Client</p>
                    <p className="truncate text-zinc-100">{it.customer_name || "—"}</p>
                    <p className="text-[10px] text-zinc-500">{it.customer_phone || ""}</p>
                  </div>
                  <div className="col-span-1">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-600">Produs</p>
                    <p className="truncate text-zinc-200">{it.first_product_name || "—"}</p>
                    <p className="text-[10px] text-zinc-500">{it.products_count} prod.</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-600">Total</p>
                    <p className="font-semibold text-zinc-100">{it.total ? `${Number(it.total).toFixed(2)} ${it.currency || "RON"}` : "—"}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SearchField({ icon: Icon, label, testId, value, onChange, placeholder, onEnter }) {
  return (
    <div>
      <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">{label}</label>
      <div className="mt-1 flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5">
        <Icon className="h-3.5 w-3.5 text-zinc-500" />
        <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === "Enter") onEnter?.(); }}
          className="flex-1 bg-transparent text-xs text-zinc-100 placeholder-zinc-600 outline-none" />
      </div>
    </div>
  );
}

// ---------- STEP 2: FORM ----------
function Step2({ form, set, setForm }) {
  const addProduct = () => setForm((f) => ({ ...f, products: [...(f.products || []), { part_number: "", name: "", quantity: 1, sale_price: 0 }] }));
  const removeProduct = (i) => setForm((f) => ({ ...f, products: (f.products || []).filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-4">
      {/* Type selector */}
      <Card title="Tip cerere">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4">
          {TYPES.map((t) => (
            <button key={t.v} data-testid={`type-${t.v}`} onClick={() => set("type", t.v)}
              className={`rounded-xl border p-3 text-left text-xs transition ${form.type === t.v ? "border-orange-500/60 bg-orange-500/10 text-orange-100" : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700"}`}>
              <div className="text-sm font-semibold">{t.l}</div>
              <div className="mt-1 text-[10px] leading-snug text-zinc-400">{t.d}</div>
            </button>
          ))}
        </div>
      </Card>

      {/* Comanda */}
      <Card title="Comandă">
        <Inp testId="f-order" label="Nr. comandă" value={form.order_id || ""} onChange={(v) => set("order_id", v.replace(/^#/, ""))} placeholder="ex: 48315216" />
      </Card>

      {/* Client */}
      <Card title="Client">
        <Row>
          <Inp testId="f-cname" label="Nume *" value={form.customer_name || ""} onChange={(v) => set("customer_name", v)} required />
          <Inp testId="f-cphone" label="Telefon" value={form.customer_phone || ""} onChange={(v) => set("customer_phone", v)} />
        </Row>
        <Row>
          <Inp testId="f-cemail" label="Email" value={form.customer_email || ""} onChange={(v) => set("customer_email", v)} placeholder="client@exemplu.ro" />
          <Inp testId="f-ciban" label="IBAN" value={form.customer_iban || ""} onChange={(v) => set("customer_iban", v.toUpperCase())} placeholder="RO00BANK..." />
        </Row>
      </Card>

      {/* Adresa unică (livrare + ridicare) */}
      <Card title="Adresă (livrare & ridicare)">
        <p className="-mt-1 mb-2 text-[10px] text-amber-300/70">
          ⚠ Cere clientului adresa exactă (mai ales dacă a livrat la easybox / locker).
        </p>
        {/* Toggle Domiciliu vs Easybox — DOAR la Cerere de Livrare (2026-09-01). */}
        {form.type === "livrare" && (
          <div className="mb-3 flex items-center gap-2">
            <button type="button" onClick={() => set("delivery_mode", "home")}
              data-testid="f-delivery-mode-home"
              className={`rounded-md border px-3 py-1.5 text-[11px] font-semibold ${(form.delivery_mode || "home") === "home" ? "border-orange-500/60 bg-orange-500/15 text-orange-200" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`}>
              🏠 Livrare la domiciliu
            </button>
            <button type="button" onClick={() => set("delivery_mode", "locker")}
              data-testid="f-delivery-mode-locker"
              className={`rounded-md border px-3 py-1.5 text-[11px] font-semibold ${form.delivery_mode === "locker" ? "border-orange-500/60 bg-orange-500/15 text-orange-200" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`}>
              📦 Livrare la Easybox
            </button>
          </div>
        )}
        {form.type === "livrare" && form.delivery_mode === "locker" ? (
          <>
            <Row3>
              <CountyCityPicker
                county={form.customer_county || ""}
                city={form.customer_city || ""}
                onChange={({ county, city }) => setForm((f) => ({ ...f, customer_county: county, customer_city: city, customer_locker_id: "", customer_locker_name: "" }))}
              />
              <div />
            </Row3>
            <LockerPicker
              city={form.customer_city || ""}
              value={form.customer_locker_id || ""}
              onChange={({ id, name, address }) => setForm((f) => ({ ...f, customer_locker_id: id, customer_locker_name: name, customer_address: address || f.customer_address }))}
            />
          </>
        ) : (
          <>
            <Inp testId="f-caddr" label="Adresă" value={form.customer_address || ""} onChange={(v) => set("customer_address", v)} />
            <Row3>
              <CountyCityPicker
                county={form.customer_county || ""}
                city={form.customer_city || ""}
                onChange={({ county, city }) => setForm((f) => ({ ...f, customer_county: county, customer_city: city }))}
              />
              <Inp testId="f-czip" label="Cod poștal" value={form.customer_postal_code || ""} onChange={(v) => set("customer_postal_code", v)} />
            </Row3>
          </>
        )}
      </Card>

      {/* Produse cu autocomplete */}
      <Card title={`Produse (${form.products?.length || 0})`} headerExtra={
        <button data-testid="f-add-product" onClick={addProduct}
          className="rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-[10px] text-zinc-300 hover:border-orange-500/40 hover:text-orange-300">
          + Produs
        </button>
      }>
        {(!form.products || form.products.length === 0) && <p className="text-xs text-zinc-500">Niciun produs. Apasă „+ Produs”.</p>}
        <div className="space-y-2">
          {(form.products || []).map((p, i) => (
            <ProductAutocompleteRow key={i} index={i} product={p}
              onUpdate={(patch) => setForm((f) => ({ ...f, products: f.products.map((pp, idx) => idx === i ? { ...pp, ...patch } : pp) }))}
              onRemove={() => removeProduct(i)} />
          ))}
        </div>
      </Card>

      {/* Problem */}
      <Card title="Problema reclamată de client *">
        <textarea data-testid="f-problem" value={form.problem || ""} onChange={(e) => set("problem", e.target.value)}
          rows={3} placeholder="Descrie problema cu cuvintele clientului..."
          className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-100 outline-none focus:border-orange-500/40" />
      </Card>
    </div>
  );
}

function LockerPicker({ city, value, onChange }) {
  const [lockers, setLockers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  // After the user picks a locker we collapse the list and show the chosen
  // locker's name in the search bar. The list reappears if the user types
  // (treating it like a native combobox).
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    if (!city) { setLockers([]); return; }
    setLoading(true);
    axios.get(`${API}/sameday/lockers`, { params: { country: "ro", city } })
      .then((r) => setLockers(r.data?.lockers || []))
      .catch(() => setLockers([]))
      .finally(() => setLoading(false));
  }, [city]);
  // Reset collapsed/search when city changes so the user can pick again.
  useEffect(() => { setQ(""); setCollapsed(false); }, [city]);
  const filtered = q ? lockers.filter((l) =>
    (l.name || "").toLowerCase().includes(q.toLowerCase()) ||
    (l.address || "").toLowerCase().includes(q.toLowerCase())
  ) : lockers;
  const selectedLocker = lockers.find((l) => String(l.lockerId || l.id) === String(value));
  return (
    <div className="mt-2">
      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Easybox {city ? `în ${city}` : "(selectează orașul întâi)"}</label>
      <div className="relative">
        <input type="text" value={q}
          onChange={(e) => { setQ(e.target.value); setCollapsed(false); }}
          onFocus={() => setCollapsed(false)}
          data-testid="f-locker-search"
          placeholder={city ? "Caută după nume sau adresă..." : "Selectează întâi orașul"}
          disabled={!city}
          className="mb-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 pr-16 text-xs text-zinc-100 outline-none focus:border-orange-500/40 disabled:opacity-50" />
        {value && (
          <button
            type="button"
            onClick={() => { onChange({ id: "", name: "", address: "" }); setQ(""); setCollapsed(false); }}
            data-testid="f-locker-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-0.5 text-[10px] font-medium text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
            title="Schimbă easybox"
          >
            Schimbă
          </button>
        )}
      </div>
      {collapsed && selectedLocker ? null : loading ? (
        <p className="text-[11px] text-zinc-500">Se încarcă easybox-urile...</p>
      ) : city && filtered.length === 0 ? (
        <p className="text-[11px] text-zinc-500">Niciun easybox găsit în {city}{q ? ` pentru „${q}"` : ""}.</p>
      ) : (
        <div className="max-h-48 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950">
          {filtered.map((l) => {
            const selected = String(l.lockerId || l.id) === String(value);
            return (
              <button key={l.lockerId || l.id} type="button"
                onClick={() => {
                  onChange({ id: String(l.lockerId || l.id), name: l.name, address: l.address });
                  setQ(l.name || "");
                  setCollapsed(true);
                }}
                data-testid={`f-locker-opt-${l.lockerId || l.id}`}
                className={`block w-full px-3 py-2 text-left text-[11px] border-b border-zinc-900 last:border-b-0 ${selected ? "bg-orange-500/15 text-orange-200" : "text-zinc-300 hover:bg-zinc-900"}`}>
                <div className="font-semibold">{l.name}</div>
                <div className="text-[10px] text-zinc-500">{l.address}</div>
              </button>
            );
          })}
        </div>
      )}
      {value && selectedLocker && (
        <p className="mt-1 text-[10px] text-emerald-400" data-testid="f-locker-selected">
          ✓ Selectat: {selectedLocker.name} · ID {value}
        </p>
      )}
    </div>
  );
}

function Card({ title, children, headerExtra }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">{title}</h4>
        {headerExtra}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ children }) { return <div className="grid grid-cols-2 gap-2">{children}</div>; }
function Row3({ children }) { return <div className="grid grid-cols-3 gap-2">{children}</div>; }

function CountyCityPicker({ county, city, onChange }) {
  const { counties, loading: cLoading } = useCounties();
  const selected = counties.find((c) => c.name === county) || null;
  const { cities, loading: ciLoading } = useCities(selected?.id);
  return (
    <>
      <label className="block">
        <span className="mb-0.5 block text-[10px] font-medium text-zinc-400">Județ</span>
        <select
          data-testid="f-ccounty"
          value={county || ""}
          onChange={(e) => onChange({ county: e.target.value, city: "" })}
          className="w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-200 focus:border-orange-500/40 focus:outline-none"
        >
          <option value="">{cLoading ? "Se încarcă..." : "— alege județ —"}</option>
          {counties.map((c) => (
            <option key={c.id} value={c.name}>{c.name}</option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="mb-0.5 block text-[10px] font-medium text-zinc-400">Oraș</span>
        <select
          data-testid="f-ccity"
          value={city || ""}
          onChange={(e) => onChange({ county, city: e.target.value })}
          disabled={!selected}
          className="w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-200 focus:border-orange-500/40 focus:outline-none disabled:opacity-50"
        >
          <option value="">{!selected ? "alege întâi județul" : ciLoading ? "Se încarcă..." : "— alege oraș —"}</option>
          {cities.map((c) => (
            <option key={c.id} value={c.name}>{c.name}</option>
          ))}
        </select>
      </label>
    </>
  );
}
function Inp({ testId, label, value, onChange, type = "text", placeholder, suffix, required, step }) {
  return (
    <div>
      <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">{label}{required && <span className="ml-1 text-rose-400">*</span>}</label>
      <div className="mt-1 flex items-center gap-1">
        <input data-testid={testId} type={type} step={step} value={value === null || value === undefined ? "" : value}
          onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="flex-1 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-orange-500/40" />
        {suffix && <span className="text-[10px] text-zinc-500">{suffix}</span>}
      </div>
    </div>
  );
}
// Search products outside React effect context to avoid lint warnings
async function _searchProducts(query) {
  try {
    const r = await axios.get(`${API}/products`, { params: { search: query, sku: query } });
    const items = r.data?.products || (Array.isArray(r.data) ? r.data : []);
    return items.slice(0, 12);
  } catch {
    return [];
  }
}

// ---------- Product autocomplete row ----------
function ProductAutocompleteRow({ index, product, onUpdate, onRemove }) {
  const [query, setQuery] = useState(product.name || product.part_number || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (!query || query.length < 2) {
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearching(true);
      _searchProducts(query).then((items) => {
        setResults(items);
        setSearching(false);
      });
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  const displayResults = (query && query.length >= 2) ? results : [];

  const pick = (p) => {
    const img = p.custom_image || p.main_image || (Array.isArray(p.images) && p.images[0]?.url) || null;
    const price = (typeof p.sale_price === "number") ? p.sale_price
                : (typeof p.price === "number") ? p.price
                : (p.price?.[0]?.sale_price ?? 0);
    onUpdate({
      part_number: p.part_number || p.part_number_key || String(p.id || ""),
      name: p.name,
      image: img,
      quantity: 1,
      sale_price: price,
    });
    setQuery(p.name);
    setOpen(false);
  };

  return (
    <div className="relative">
      <div className="grid grid-cols-12 gap-2">
        <input data-testid={`f-p-sku-${index}`} value={product.part_number || ""}
          onChange={(e) => onUpdate({ part_number: e.target.value })}
          placeholder="SKU"
          className="col-span-3 rounded border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none" />
        <div className="relative col-span-6">
          <input data-testid={`f-p-name-${index}`} value={query}
            onChange={(e) => { setQuery(e.target.value); onUpdate({ name: e.target.value }); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 180)}
            placeholder="Caută produs după nume sau SKU..."
            className="w-full rounded border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-orange-500/40" />
          {open && (query.length >= 2) && (
            <div data-testid={`f-p-suggest-${index}`} className="absolute left-0 right-0 z-30 mt-1 max-h-60 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950 shadow-2xl">
              {searching && <div className="px-3 py-2 text-[11px] text-zinc-500">Se caută...</div>}
              {!searching && displayResults.length === 0 && <div className="px-3 py-2 text-[11px] text-zinc-500">Niciun produs găsit. Poți tasta manual.</div>}
              {!searching && displayResults.map((p) => (
                <button key={p.id || p.part_number} type="button" onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-[11px] hover:bg-zinc-900">
                  {(p.custom_image || p.main_image) ? (
                    <img src={p.custom_image || p.main_image} alt="" className="h-8 w-8 shrink-0 rounded border border-zinc-800 object-cover" />
                  ) : (
                    <div className="h-8 w-8 shrink-0 rounded border border-zinc-800 bg-zinc-900" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-zinc-100">{p.name}</p>
                    <p className="font-mono text-[9px] text-zinc-500">{p.part_number || p.part_number_key || p.id}</p>
                  </div>
                  {(p.sale_price != null) && <span className="shrink-0 text-[10px] text-zinc-400">{Number(p.sale_price).toFixed(2)} RON</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <input data-testid={`f-p-qty-${index}`} type="number" value={product.quantity ?? 1}
          onChange={(e) => onUpdate({ quantity: Number(e.target.value) })}
          className="col-span-1 rounded border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-right text-xs text-zinc-100 outline-none" />
        <input data-testid={`f-p-price-${index}`} type="number" step="0.01" value={product.sale_price ?? 0}
          onChange={(e) => onUpdate({ sale_price: Number(e.target.value) })}
          className="col-span-1 rounded border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-right text-xs text-zinc-100 outline-none" />
        <button data-testid={`f-p-del-${index}`} onClick={onRemove} className="col-span-1 rounded border border-zinc-800 bg-zinc-900 text-rose-400 hover:text-rose-300">
          <X className="mx-auto h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
